import { useEffect } from "react";
import Lenis from "lenis";
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";
import { Link } from "react-router-dom";
import { useGSAP } from "@/hooks/useGSAP";

export default function NestrootAccidentalTnc() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        <section className="tw-bg-white tw-text-black tw-py-20">
          <div className="tw-max-w-5xl tw-mx-auto tw-px-6 tw-space-y-16">
            {/* Header Content */}
            <div className="tw-border-b tw-border-gray-100 tw-pb-10">
              <h1 
                className="tw-text-4xl md:tw-text-5xl tw-font-medium tw-mb-4"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                Protega × Nestroots
              </h1>
              <p 
                className="tw-text-xl tw-text-gray-500 tw-font-medium"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Accidental Damage Protection & Care Program — Terms & Conditions
              </p>
            </div>

            {/* 1. Plan Introduction */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                1. Plan Introduction
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  The Protega × Nestroots Accidental Damage Protection & Care Program (hereinafter referred to as the “Plan”) is a comprehensive post-purchase service contract designed to provide customers with protection against sudden, unforeseen, and unintentional physical damage to eligible furniture and home décor products. In addition to such protection, the Plan also includes access to an optional Care Program, which enables customers to maintain and preserve their products through periodic upkeep services during the tenure of the Plan.
                </p>
                <p>
                  This Plan is administered by EmbedMike India Private Limited (“Protega”) on behalf of Nestroots and shall, under no circumstances, be construed as a contract of insurance. The Plan is a service agreement intended to supplement, and not replace or override, any manufacturer warranty, seller guarantee, or statutory rights available to the customer under applicable laws in India. The obligations of Protega under this Plan are limited strictly to the terms set out herein.
                </p>
                <p>
                  Coverage under the Accidental Damage component of the Plan is restricted to incidents that are external, visible, sudden, and verifiable in nature, and which directly result in structural damage, breakage, or functional impairment of the covered product. Any damage that arises from gradual deterioration, inherent product defects, or causes that cannot be reasonably attributed to a specific accidental event shall not be eligible for coverage.
                </p>
                <p>
                  The Plan shall become effective from the later of the date of product delivery or the date of Plan activation and shall remain valid for a continuous and uninterrupted period of either twelve (12) months or twenty-four (24) months, depending on the option selected by the customer at the time of purchase. The Plan is valid only within the territorial jurisdiction of India, and all services shall be rendered exclusively through Protega’s authorized service network.
                </p>
                <p>
                  Each approved claim under the Accidental Damage Protection component shall be subject to a mandatory deductible, calculated as two percent (2%) of the original invoice value of the product. The deductible shall be payable by the customer prior to the initiation of any service activity.
                </p>
                <p>
                  In addition to protection, the Plan provides access to a Care Program, which is an optional, customer-initiated service offering. This Care Program is not automatically scheduled and must be availed by the customer through a service request raised via Protega’s platform. Customers are eligible to avail such Care service once per year during the Plan tenure, subject to availability and serviceability conditions. The Care Program is designed for maintenance and upkeep and does not constitute repair, replacement, or damage coverage.
                </p>
                <p>
                  All services under this Plan are subject to the condition that the product has been used, maintained, and handled in accordance with manufacturer guidelines and reasonable standards of care. Any deviation from such prescribed usage may result in denial of service.
                </p>
              </div>
            </div>

            {/* 2. Customer Responsibilities */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                2. Customer Responsibilities
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  In order to remain eligible for benefits under this Plan, the customer is required to comply with certain responsibilities that are fundamental to the fair and efficient administration of services. The customer shall, at all times, retain valid proof of purchase, including the original invoice and Plan confirmation documentation, and shall produce the same upon request at the time of claim or service initiation.
                </p>
                <p>
                  The customer is required to notify Protega of any accidental damage incident within seventy-two (72) hours from the time of occurrence. Such notification must be accompanied by a clear and accurate description of the incident, along with supporting evidence in the form of photographs and/or videos that adequately depict the nature and extent of the damage. The information provided must be complete, truthful, and consistent with the actual circumstances of the incident.
                </p>
                <p>
                  Upon occurrence of damage, the customer must take reasonable steps to prevent further deterioration of the product and must immediately discontinue usage if continued use may aggravate the damage or pose safety risks. The product must not be opened, altered, repaired, or tampered with by any unauthorized individual or service provider, as such actions may void eligibility under the Plan.
                </p>
                <p>
                  The customer is expected to use the product strictly for its intended residential purpose and in accordance with manufacturer instructions. Exposure to abnormal usage conditions, improper handling, or negligent care may lead to claim rejection.
                </p>
                <p>
                  In relation to the Care Program, the customer must proactively initiate the service request through Protega’s official channels. It shall be the responsibility of the customer to ensure that the product is accessible for servicing and that appropriate arrangements are made to facilitate inspection or maintenance visits. Failure to cooperate or provide access may result in cancellation of the scheduled service.
                </p>
              </div>
            </div>

            {/* 3. How to File a Claim */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                3. How to File a Claim
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  In the event of accidental damage, the customer must initiate a claim by contacting Protega through its designated service channels, which may include the online customer portal, email, phone, or WhatsApp support. All claims must be reported within seventy-two (72) hours of the incident.
                </p>
                <p>
                  Upon initiation, the customer shall be required to submit relevant documentation, including proof of purchase, Plan confirmation, visual evidence of damage, and a written account of the incident. Protega reserves the right to request additional documentation or clarification where necessary to validate the claim.
                </p>
                <p>
                  Once all required documentation has been received, the claim shall undergo a technical assessment process, which includes evaluation of the cause, nature, and extent of the damage, as well as verification of eligibility under the Plan. This assessment is typically completed within twenty-four (24) to forty-eight (48) hours.
                </p>
                <p>
                  Upon approval of the claim, the customer shall be required to pay the applicable deductible. Thereafter, Protega shall arrange for pickup or inspection of the product through its authorized logistics partners. Based on the findings of the inspection and commercial feasibility, Protega may proceed with repair, replacement, or settlement.
                </p>
                <p>
                  The overall claim lifecycle shall be governed by defined service timelines, with the objective of ensuring resolution within seven (7) to ten (10) working days from the date of product pickup, subject to operational constraints and availability of parts.
                </p>
              </div>
            </div>

            {/* 4. Definitions */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                4. Definitions
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <ul className="tw-list-disc tw-pl-6 tw-space-y-3">
                  <li><strong>“Accidental Damage”</strong> shall mean sudden, unforeseen, and unintended physical damage caused by an external event beyond the control of the customer, resulting in functional or structural impairment of the product.</li>
                  <li><strong>“Care Program”</strong> shall refer to the optional, on-demand maintenance and upkeep service made available to customers under this Plan, which may be availed once per year during the Plan tenure.</li>
                  <li><strong>“Administrator”</strong> shall mean EmbedMike India Private Limited (Protega), which is responsible for the administration, management, and execution of services under this Plan.</li>
                  <li><strong>“Authorized Service Partner”</strong> shall refer to any third-party service provider duly appointed and authorized by Protega to carry out inspection, repair, maintenance, or replacement services.</li>
                  <li><strong>“Beyond Economic Repair (BER)”</strong> shall describe a condition wherein the cost of repair exceeds the reasonable or commercially viable value of the product.</li>
                  <li><strong>“Sum Insured”</strong> shall mean the original invoice value of the product, which represents the maximum liability of Protega under this Plan.</li>
                  <li><strong>“Policy Term”</strong> shall refer to the duration of coverage selected by the customer at the time of purchase, being either twelve (12) or twenty-four (24) months.</li>
                  <li><strong>“Deductible”</strong> shall mean the amount payable by the customer as a prerequisite to claim processing.</li>
                  <li><strong>“Depreciation”</strong> shall mean the reduction in value of the product over time, calculated at 10% per year on a straight-line basis, applicable in cases of total loss or replacement settlement.</li>
                  <li><strong>“Salvage Value”</strong> shall mean the residual value of the damaged product, and in case of total loss settlement, a flat deduction of 3% of the invoice value shall be applicable towards salvage recovery.</li>
                </ul>
              </div>
            </div>

            {/* 5. Eligibility */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                5. Eligibility
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  This Plan shall be applicable only to products that meet the prescribed eligibility criteria. The product must have been purchased through Nestroots’ authorized sales channels and must be new, unused, and free from any pre-existing damage at the time of Plan activation. Additionally, only products with an invoice value exceeding ₹5,000 shall be eligible for Accidental Damage Protection under this Plan.
                </p>
                <p>
                  The Plan is intended strictly for personal and residential usage. Products used for commercial, institutional, rental, or public purposes shall not be eligible.
                </p>
                <p>
                  Eligibility for the Care Program is conditional upon the Plan being active. Customers may avail the Care service once per year per product, and such service must be explicitly requested through the designated platform.
                </p>
              </div>
            </div>

            {/* 6. What is Covered */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                6. What is Covered
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  Subject to compliance with all terms and conditions, the Plan provides coverage against accidental physical damage that directly affects the usability or structural integrity of the product. This includes, but is not limited to, damage caused by accidental impacts, drops, falls, liquid spills, and pet-related incidents, provided such damage is sudden and unintentional.
                </p>
                <p>
                  The Care Program, as an additional service component, includes maintenance activities such as cleaning, polishing, inspection of joints and fittings, tightening of accessible hardware, and general upkeep guidance. These services are preventive in nature and are intended to enhance the longevity of the product.
                </p>
              </div>
            </div>

            {/* 7. What is Not Covered */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                7. What is Not Covered
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  The Plan does not cover damage arising from manufacturing defects, inherent product faults, normal wear and tear, corrosion, or gradual deterioration over time. Cosmetic damage that does not impact functionality, as well as damage resulting from misuse, abuse, or negligence, shall also be excluded.
                </p>
                <p>
                  Events such as fire, floods, earthquakes, theft, or loss are not covered under this Plan. Additionally, any damage resulting from unauthorized repairs, modifications, or usage outside manufacturer guidelines shall be excluded.
                </p>
                <p>
                  The Care Program does not include structural repairs, replacement of parts, or restoration of severely damaged items. It is strictly limited to maintenance and upkeep services.
                </p>
              </div>
            </div>

            {/* 8. Service Procedures and Limit of Liability */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                8. Service Procedures and Limit of Liability
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  The benefits under this Plan are subject to defined service procedures and limitations. Only one (1) approved claim shall be permitted per product during the Plan tenure. The total liability of Protega shall not exceed the original invoice value of the product under any circumstances.
                </p>
                <p>
                  In cases where the product is deemed Beyond Economic Repair (BER) and a total loss settlement is processed, the claim amount shall be calculated after applying applicable depreciation at 10% per year and a salvage deduction of 3% of the invoice value.
                </p>
                <p>
                  Upon completion of a claim through repair, replacement, or settlement, the Plan shall automatically terminate with respect to the covered product. All decisions regarding the mode of resolution shall be made at the sole discretion of Protega and shall be final and binding.
                </p>
              </div>
            </div>

            {/* 9. Cancellation */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                9. Cancellation
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  The customer may cancel the Plan within seven (7) days of purchase, provided that no claim has been initiated and no service has been availed. Upon successful cancellation within this period, the customer may be eligible for a full refund, subject to verification and applicable deductions, if any.
                </p>
                <p>
                  Once a claim has been registered or any benefit under the Plan has been utilized, including availing of the Care Program, the Plan shall become non-refundable and non-cancellable. Protega reserves the right to cancel the Plan at any time in cases involving fraud, misrepresentation, deliberate concealment of material facts, or misuse of services.
                </p>
                <p>
                  In the event of cancellation initiated by Protega, the customer shall be notified, and any refund, if applicable, shall be processed in accordance with internal policies and timelines.
                </p>
              </div>
            </div>

            {/* 10. Conditions */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                10. Conditions
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  This Plan is strictly non-transferable and shall be valid only for the original purchaser of the product. The Plan cannot be renewed, extended, or reinstated once it has expired or been terminated. All services under the Plan are restricted to serviceable locations within India and are subject to logistical feasibility.
                </p>
                <p>
                  The customer agrees that all decisions made by Protega in relation to claim assessment, service eligibility, and resolution shall be final and binding. The customer further agrees to cooperate fully throughout the service lifecycle and to comply with all procedural requirements.
                </p>
                <p>
                  The Care Program is optional in nature and must be actively requested by the customer. It is limited to one service per year and does not accumulate or carry forward if unused. The Care Program does not alter or extend the coverage provided under the Accidental Damage component.
                </p>
              </div>
            </div>

            {/* 11. Legal Disclosures */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                11. Legal Disclosures
              </h2>
              <div className="tw-space-y-4 tw-text-[17px] tw-text-[#2D2D2D] tw-leading-[1.7]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  This Plan shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Plan shall be subject to resolution through arbitration in accordance with the Arbitration and Conciliation Act, 1996. The arbitration proceedings shall be conducted in English and shall take place within India.
                </p>
                <p>
                  Under no circumstances shall Protega or Nestroots be liable for any indirect, incidental, special, or consequential damages, including but not limited to loss of use, loss of business, loss of revenue, or inconvenience arising from product failure or service delays.
                </p>
                <p>
                  Protega shall not be held liable for delays or failures in performance resulting from events beyond its reasonable control, including but not limited to natural disasters, logistical disruptions, labor issues, or governmental actions.
                </p>
                <p>
                  Customer data collected in the course of service delivery may be used for operational purposes and may be shared with authorized partners strictly for execution of services, in accordance with applicable data protection laws.
                </p>
              </div>
            </div>

            {/* 12. Frequently Asked Questions (FAQ) */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-2xl md:tw-text-[28px] tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                12. Frequently Asked Questions (FAQ)
              </h2>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-12 tw-gap-y-8 tw-text-[17px] tw-text-[#2D2D2D]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {[
                  { q: "What does the Accidental Damage Protection cover?", a: "The Plan covers sudden, unforeseen, and unintentional physical damage to the product caused by external events such as impacts, drops, liquid spills, or similar incidents that result in functional or structural impairment." },
                  { q: "Does this Plan cover manufacturing defects?", a: "No. Manufacturing defects are covered under the manufacturer’s warranty." },
                  { q: "What is the Care Program?", a: "It is an optional maintenance service including cleaning, polishing, and upkeep." },
                  { q: "Is Care automatically scheduled?", a: "No. It must be requested by the customer." },
                  { q: "How many Care visits are allowed?", a: "One per year." },
                  { q: "How many claims are allowed?", a: "One claim per Plan." },
                  { q: "Is there a deductible?", a: "Yes, 2% of invoice value." },
                  { q: "What if repair is not possible?", a: "Replacement or reimbursement will be provided after depreciation and salvage deduction." },
                  { q: "Are cosmetic damages covered?", a: "No." },
                  { q: "Is pet damage covered?", a: "Yes, if accidental." },
                  { q: "What is resolution timeline?", a: "7–10 working days." },
                  { q: "Can I repair locally?", a: "No." },
                  { q: "Is theft covered?", a: "No." },
                  { q: "What happens after claim?", a: "Plan terminates." },
                  { q: "Can I cancel?", a: "Yes, within 7 days if unused." },
                  { q: "Does Care include repair?", a: "No." },
                  { q: "Is depreciation applied?", a: "Yes, 10% per year in total loss." },
                  { q: "Is salvage deduction applied?", a: "Yes, 3% in total loss cases." }
                ].map((faq, idx) => (
                  <div key={idx} className="tw-space-y-2">
                    <p className="tw-font-bold tw-text-black">Q{idx + 1}. {faq.q}</p>
                    <p className="tw-text-gray-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 13. Service Commitment */}
            <div className="tw-space-y-6">
              <h2 
                className="tw-text-3xl tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                13. Service Commitment
              </h2>
              <div className="tw-space-y-4 tw-text-lg tw-text-gray-700 tw-leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <p>
                  Protega is committed to delivering a seamless, transparent, and efficient service experience to all customers. Claims shall be acknowledged within twenty-four (24) hours, and assessment completed within forty-eight (48) hours. Pickup is arranged within 2–4 days, and final resolution within 7–10 working days.
                </p>
                <p>
                  Customers receive continuous updates, and escalation support is available. Care services are scheduled within 48 hours of request.
                </p>
              </div>
            </div>

            {/* 14. Contact Us */}
            <div className="tw-space-y-6 tw-pt-10">
              <h2 
                className="tw-text-3xl tw-font-medium tw-text-black"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                14. Contact Us
              </h2>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8 tw-text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <div className="tw-space-y-4">
                  <p className="tw-flex tw-flex-col">
                    <span className="tw-text-sm tw-text-gray-400 tw-uppercase tw-tracking-wider">Email Support</span>
                    <a href="mailto:support@protega.ai" className="tw-text-black tw-font-medium hover:tw-text-[#916B52] tw-transition-colors">support@protega.ai</a>
                  </p>
                  <p className="tw-flex tw-flex-col">
                    <span className="tw-text-sm tw-text-gray-400 tw-uppercase tw-tracking-wider">Customer Portal</span>
                    <Link to="https://www.protega.ai/customer-login" className="tw-text-black tw-font-medium hover:tw-text-[#916B52] tw-transition-colors">www.protega.ai/customer-login</Link>
                  </p>
                </div>
                <div className="tw-space-y-4">
                  <p className="tw-flex tw-flex-col">
                    <span className="tw-text-sm tw-text-gray-400 tw-uppercase tw-tracking-wider">WhatsApp Support</span>
                    <a href="https://wa.me/919324095283" target="_blank" rel="noopener noreferrer" className="tw-text-black tw-font-medium hover:tw-text-[#916B52] tw-transition-colors">+91 93240 95283</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
